import * as api from "@/api";
import QueryDataManager from "@/classes/query-data-manager";
import styles from "@/components/ai-tool-item.module.css";
import { globals } from "@/globals";
import * as signals from "@/signals";
import { AiToolInfo } from "@/types";
import { getRedirectLink, logIn, showDialog, showMenu } from "@/utils";
import { faPlus, faSquarePlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { t } from "i18next";
import { JSX, MouseEvent } from "react";

interface AiToolItemProps {
	item: AiToolInfo;
}

function AiToolItem({ item }: AiToolItemProps): JSX.Element {
	const { categories, icon, isPaid, redirectId, title, url } = item;

	const queryClient = useQueryClient();

	const categoryTagElements = categories.map((item) => (
		<div
			className={styles["tag"]}
			key={item}
		>
			{t(item)}
		</div>
	));

	const addToShortcuts = async (
		event: MouseEvent<HTMLButtonElement>,
	): Promise<void> => {
		event.preventDefault();
		if (!signals.login.value.username) {
			logIn();
			return;
		}
		signals.isLoadingShown.value = true;
		try {
			await new QueryDataManager(queryClient).createBookmark({
				icon: icon,
				itemType: "bookmark",
				title: title,
				url: ((): string => {
					if (redirectId) {
						return globals.REDIRECT_PREFIX + redirectId;
					}
					if (!url) {
						throw new Error("No URL provided");
					}
					return url;
				})(),
			});
			signals.greetingText.value = t("addedToShortcuts") + " ￣ω￣=";
		} catch (error) {
			void api.handleApiError(error);
		} finally {
			signals.isLoadingShown.value = false;
		}
	};

	const handleContextMenu = (event: MouseEvent<HTMLAnchorElement>): void => {
		showMenu(event, [
			{
				content: {
					icon: faSquarePlus,
					text: t("addToShortcuts"),
				},
				onClick: (): void => {},
				when: true,
			},
		]);
	};

	const handlePaidTagClick = (event: MouseEvent<HTMLButtonElement>): void => {
		event.preventDefault();
		void showDialog(t("paidAiToolsDescription"), {
			title: t("inappPurchases"),
		});
	};

	return (
		<a
			className={styles["ai-tool-item"]}
			href={redirectId ? getRedirectLink(redirectId) : url}
			onContextMenu={handleContextMenu}
			rel="noreferrer"
			target="_blank"
		>
			<div className={styles["main-info-container"]}>
				<img
					alt={item.title}
					height={48}
					loading="lazy"
					src={icon}
					width={48}
				/>
				<div>
					<div className={styles["title"]}>{item.title}</div>
					<div className={styles["description"]}>
						{item.description}
					</div>
				</div>
			</div>
			<div className={styles["tag-container"]}>
				{categoryTagElements}
				{isPaid && (
					<button
						className={clsx(styles["tag"], styles["paid-tag"])}
						onClick={handlePaidTagClick}
						type="button"
					>
						{t("inappPurchases")}
						<i>&#xe782;</i>
					</button>
				)}
			</div>
			<button
				className={styles["add-button"]}
				onClick={(event) => void addToShortcuts(event)}
				title={t("addToShortcuts")}
				type="button"
			>
				<FontAwesomeIcon icon={faPlus} />
			</button>
		</a>
	);
}

export default AiToolItem;
